import { Injectable, inject } from '@angular/core';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest, PopupRequest, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { Observable, BehaviorSubject, filter, takeUntil, Subject, from, finalize } from 'rxjs';
import { loginRequest } from '../config/auth.config';
import { MsalInitService } from './msal-init.service';
import { PermissionService } from './permission.service';
import { environment } from '../../environments/environment';


export interface UserProfile {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
  roles: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly _destroying$ = new Subject<void>();
  private userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private isInitializedSubject = new BehaviorSubject<boolean>(false);
  private pendingInteraction = false;
  private permissionService = inject(PermissionService);
  private _cachedMsalToken: string | null = null;

  
  public userProfile$ = this.userProfileSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public isInitialized$ = this.isInitializedSubject.asObservable();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private msalInitService: MsalInitService
  ) {
    this.initializeAuth();
  }

  private initializeAuth(): void {
    console.log('MSAL initializeAuth');
    this.msalInitService.getMsalInstance().then(() => {
      this.checkAndSetActiveAccount();
      this.isInitializedSubject.next(true);
    }).catch(error => {
      console.error('Failed to initialize MSAL:', error);
      this.isInitializedSubject.next(true); // Still set to true to unblock guards, but auth will be false
    });
    
  }

  private checkAndSetActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    
    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    const account = this.msalService.instance.getActiveAccount();
    this.isAuthenticatedSubject.next(!!account);
    
    if (account) {
      this.loadUserProfile(account);
    } else {
      this.userProfileSubject.next(null);
    }
  }

  private loadUserProfile(account: AccountInfo): void {
    // Extract user information from the account
    const userProfile: UserProfile = {
      id: account.localAccountId,
      displayName: account.name || '',
      mail: account.username,
      userPrincipalName: account.username,
      jobTitle: (account.idTokenClaims?.['jobTitle'] as string) || '',
      department: (account.idTokenClaims?.['department'] as string) || '',
      roles: this.extractRoles(account)
    };

    this.userProfileSubject.next(userProfile);

    // Cache MSAL access token so the HTTP interceptor can inject it synchronously
    // as Authorization: Bearer on every request (required by desarrolla-agent on port 8001)
    this.msalService.acquireTokenSilent({ scopes: ['user.read'], account }).subscribe({
      next: res => { this._cachedMsalToken = res.accessToken; },
      error: () => {}
    });

    this.permissionService.loadPermissions(account.username).subscribe({
      next: () => {
        // Registrar el inicio de sesión una sola vez por sesión del navegador (para evitar logs duplicados en F5/refresh)
        if (sessionStorage.getItem('login_logged') !== 'true') {
          sessionStorage.setItem('login_logged', 'true');
          this.permissionService.logUserLogin().subscribe({
            next: () => {
              console.log('[AuthService] Inicio de sesión registrado en auditoría.');
            },
            error: (err) => {
              console.error('[AuthService] Error registrando inicio de sesión:', err);
              sessionStorage.removeItem('login_logged');
            }
          });
        }
      }
    });

  }

  private extractRoles(account: AccountInfo): string[] {
    // Extract roles from token claims
    const roles = (account.idTokenClaims?.['roles'] as string[]) || [];
    const groups = (account.idTokenClaims?.['groups'] as string[]) || [];
    
    // Define role mappings based on your Azure AD configuration
    const roleMapping: { [key: string]: string } = {
      'admin-group-id': 'admin',
      'editor-group-id': 'content.editor',
      'viewer-group-id': 'viewer'
    };

    const userRoles: string[] = [];
    
    // Map groups to roles
    if (Array.isArray(groups)) {
      groups.forEach((groupId: string) => {
        if (roleMapping[groupId]) {
          userRoles.push(roleMapping[groupId]);
        }
        // Also check if the group name/ID contains 'admin' or 'editor' literals (flexible)
        const lowerId = groupId.toLowerCase();
        if (lowerId.includes('admin')) userRoles.push('admin');
        if (lowerId.includes('editor')) userRoles.push('content.editor');
      });
    }

    // Add direct roles
    if (Array.isArray(roles)) {
      roles.forEach(role => {
        const lowerRole = role.toLowerCase();
        if (lowerRole.includes('admin')) userRoles.push('admin');
        else if (lowerRole.includes('editor')) userRoles.push('content.editor');
        else userRoles.push(lowerRole);
      });
    }

    const uniqueRoles = Array.from(new Set(userRoles));
    return uniqueRoles.length > 0 ? uniqueRoles : ['viewer'];
  }

  // Login methods
  loginRedirect(request?: any): void {
    try {
      const finalRequest = { ...loginRequest, ...request };
      this.msalService.loginRedirect(finalRequest);
    } catch (error) {
      console.error('Login redirect error:', error);
    }
  }

  ssoSilent(request: any): Observable<AuthenticationResult> {
    return this.msalService.ssoSilent(request);
  }

  loginPopup(): Observable<AuthenticationResult> {
    if (this.pendingInteraction) {
      console.warn('Interaction already in progress, skipping popup login');
      return new Observable(observer => {
        observer.error('Interaction already in progress');
      });
    }

    this.pendingInteraction = true;
    return this.msalService.loginPopup(loginRequest).pipe(
      finalize(() => {
        this.pendingInteraction = false;
      })
    );
  }

  logout(localOnly: boolean = false): void {
    if (this.pendingInteraction) {
      console.warn('Interaction in progress, waiting before logout');
      // Wait for current interaction to complete
      setTimeout(() => this.logout(localOnly), 1000);
      return;
    }

    try {
      this.pendingInteraction = true;
      this.clearSsoCookie();
      this.permissionService.clearPermissions();
      
      if (localOnly) {
        // Limpiar caché local de MSAL sin redirigir a Microsoft (evita la pantalla de 'Cierre sus ventanas' cuando la sesión ya expiró)
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && (key.includes('msal') || key.includes('microsoft') || key.includes('azure'))) {
            localStorage.removeItem(key);
          }
        }
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key && (key.includes('msal') || key.includes('microsoft') || key.includes('azure'))) {
            sessionStorage.removeItem(key);
          }
        }
        window.location.href = '/';
      } else {
        this.msalService.logoutRedirect({
          postLogoutRedirectUri: window.location.origin
        });
      }

    } catch (error) {
      console.error('Logout error:', error);
      this.pendingInteraction = false;
    }
  }

  logoutPopup(): Observable<void> {
    if (this.pendingInteraction) {
      return new Observable(observer => {
        observer.error('Interaction already in progress');
      });
    }

    this.pendingInteraction = true;
    this.clearSsoCookie();
    this.permissionService.clearPermissions();
    return this.msalService.logoutPopup().pipe(
      finalize(() => {
        this.pendingInteraction = false;
      })
    );
  }

  private clearSsoCookie(): void {
    const cookieName = environment.ssoCookieName;
    const domain = environment.ssoCookieDomain;
    
    // Clear with domain
    document.cookie = `${cookieName}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax`;
    // Clear sin domain explícito por si hay fallback a localhost local
    document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax`;
  }

  // Token management
  getAccessToken(scopes: string[] = ['user.read']): Observable<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      throw new Error('No active account found');
    }

    const request = {
      scopes: scopes,
      account: account
    };

    return new Observable(observer => {
      this.msalService.acquireTokenSilent(request).subscribe({
        next: (response: AuthenticationResult) => {
          observer.next(response.accessToken);
          observer.complete();
        },
        error: (error) => {
          // If silent token acquisition fails, try interactive
          this.msalService.acquireTokenPopup(request).subscribe({
            next: (response: AuthenticationResult) => {
              observer.next(response.accessToken);
              observer.complete();
            },
            error: (popupError) => {
              observer.error(popupError);
            }
          });
        }
      });
    });
  }

  getCachedMsalToken(): string | null {
    return this._cachedMsalToken;
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    const userProfile = this.userProfileSubject.value;
    return userProfile?.roles.includes(role) || false;
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles: string[]): boolean {
    const userProfile = this.userProfileSubject.value;
    if (!userProfile) return false;
    return roles.some(role => userProfile.roles.includes(role));
  }

  // Get current user profile
  getCurrentUser(): UserProfile | null {
    return this.userProfileSubject.value;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  // Refresh token
  refreshToken(): Observable<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      throw new Error('No active account found');
    }

    if (this.pendingInteraction) {
      return new Observable(observer => {
        observer.error('Interaction already in progress');
      });
    }

    const request = {
      scopes: ['user.read'],
      account: account,
      forceRefresh: true
    };

    return new Observable(observer => {
      this.msalService.acquireTokenSilent(request).subscribe({
        next: (response: AuthenticationResult) => {
          observer.next(response.accessToken);
          observer.complete();
        },
        error: (error) => {
          console.error('Silent token refresh failed:', error);
          // If silent refresh fails and no interaction is in progress, try interactive
          if (!this.pendingInteraction) {
            this.pendingInteraction = true;
            this.msalService.acquireTokenPopup(request).subscribe({
              next: (response: AuthenticationResult) => {
                this.pendingInteraction = false;
                observer.next(response.accessToken);
                observer.complete();
              },
              error: (popupError) => {
                this.pendingInteraction = false;
                observer.error(popupError);
              }
            });
          } else {
            observer.error(error);
          }
        }
      });
    });
  }

  // Check if interaction is in progress
  isInteractionInProgress(): boolean {
    return this.pendingInteraction;
  }

  // Wait for interaction to complete
  waitForInteractionToComplete(): Observable<void> {
    return new Observable(observer => {
      if (!this.pendingInteraction) {
        observer.next();
        observer.complete();
        return;
      }

      const checkInterval = setInterval(() => {
        if (!this.pendingInteraction) {
          clearInterval(checkInterval);
          observer.next();
          observer.complete();
        }
      }, 100);

      // Timeout after 30 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        observer.error('Timeout waiting for interaction to complete');
      }, 30000);
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}